import { APP_INITIALIZER, NgModule } from '@angular/core';
import { TitleStrategy } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ToastrModule } from 'ngx-toastr';
import { AgCrmModule } from './ag-crm/ag-crm.module';
import { LayoutsModule } from './layouts/layouts.module';
import { PageTitleService } from 'ag-common-svc/public-api';
import { ScreenService } from './shared/services/util/screen.service';
import { AppInfoService } from './shared/services/util/app-info.service';
import { RegistrationsModule } from './ag-crm/registrations/registrations.module';
import { DxFormModule, DxLoadIndicatorModule } from 'devextreme-angular';
import { AdminCrmModule } from './admin-crm/admin-crm.module';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { environment } from 'src/environments/environment';
import {
  AuthService,
  REPORTING_API_CONFIG,
  provideAgentSearch,
  ReportingApiAuthInterceptor,
  ReportingApiBaseUrlInterceptor,
} from 'ag-common-svc/public-api';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { AgentResearchLauncherComponent } from './agent-research/agent-research-launcher.component';
import './date.prototype';
import { DatePipe } from '@angular/common';
import { IdleModule } from 'ag-common-svc/lib/components/idle/idle.module';
import { ARMAuthService } from './shared/services/util/arm-auth.service';
import {
  STRIPE_PUBLIC_KEY_LIVE,
  STRIPE_PUBLIC_KEY_TEST,
} from 'ag-common-svc/lib/components/ag-stripe-modal/ag-stripe-modal.models';
import { NgxStripeModule } from 'ngx-stripe';
import { ARMPermissionsService } from './shared/services/util/arm-permissions.service';
import { PermissionsService } from 'ag-common-svc/lib/services/permissions.service';
import {
  CACHE_SIZE_UNLIMITED,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from 'ag-common-lib/lib/models/utils/google-maps-injection-token';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { USE_CLOUD_FUNCTIONS_EMULATOR } from 'ag-common-svc/lib/injections/use-cloud-functions-emulator';
import { USE_TEMP_MEDIA_UPLOAD } from 'ag-common-svc/lib/injections/use-temp-media-upload';
import { TempReactHostComponent } from './temp-react-host/temp-react-host.component';

export const fireBaseApp: FirebaseApp = initializeApp(environment.common);

initializeFirestore(fireBaseApp, {
  experimentalForceLongPolling: true,
  ignoreUndefinedProperties: true,
  localCache: persistentLocalCache({
    cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  }),
});

@NgModule({
  declarations: [AppComponent, MainLayoutComponent, TempReactHostComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ToastrModule.forRoot(),
    AgCrmModule,
    AdminCrmModule,
    LayoutsModule,
    AgentResearchLauncherComponent,
    RegistrationsModule,
    DxFormModule,
    DxLoadIndicatorModule,
    IdleModule,
    NgxStripeModule.forRoot(),
  ],
  providers: [
    ScreenService,
    AppInfoService,
    PageTitleService,
    { provide: TitleStrategy, useExisting: PageTitleService },
    { provide: HTTP_INTERCEPTORS, useClass: ReportingApiBaseUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ReportingApiAuthInterceptor, multi: true },
    {
      provide: FIREBASE_APP,
      useValue: fireBaseApp,
    },
    {
      provide: USE_CLOUD_FUNCTIONS_EMULATOR,
      useValue: environment.firebaseFunctions.useFunctionsEmulator,
    },
    {
      provide: USE_TEMP_MEDIA_UPLOAD,
      useValue: environment.useTempMediaUpload,
    },
    {
      provide: AuthService,
      useClass: ARMAuthService,
    },
    {
      provide: ARMAuthService,
      useExisting: AuthService,
    },
    { provide: PermissionsService, useClass: ARMPermissionsService },
    {
      provide: STRIPE_PUBLIC_KEY_LIVE,
      useValue: environment.stripe_payments_public_key_live,
    },
    {
      provide: STRIPE_PUBLIC_KEY_TEST,
      useValue: environment.stripe_payments_public_key_test,
    },
    {
      provide: GOOGLE_MAPS_API_KEY,
      useValue: environment.google.mapsAPIKey,
    },
    {
      provide: GOOGLE_MAPS_MAP_ID,
      useValue: environment.google.mapId,
    },
    {
      provide: REPORTING_API_CONFIG,
      useValue: environment.reportingApi,
    },
    // Binds AgentSearchService to the SQL-backed implementation.
    ...provideAgentSearch(),
    ARMPermissionsService,
    DatePipe,
    /**
     * Hold bootstrap until the permission map has resolved once.
     *
     * Beyond avoiding UI that pops in as permissions arrive, this fixes a real bug:
     * the landing-page picker chooses the first route the user is PERMITTED to see,
     * so running it against an unresolved map silently fell through to the agents
     * list for everyone.
     *
     * Safe to block on: the stream emits an empty map for a signed-out user and
     * fails closed if the identity API is unreachable, so it always settles.
     */
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [PermissionsService],
      useFactory: (permissionsService: PermissionsService) => () =>
        firstValueFrom(permissionsService.permissions$).catch(() => undefined),
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
